import { useEffect, useState } from "react";
import { apiRequest } from "../services/apiClient";

export default function AdminDatabaseHealth() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [importInfo, setImportInfo] = useState(null);

  async function check() {
    setLoading(true);
    try { setStatus(await apiRequest("/admin/database/status")); }
    catch (error) { setMessage(error.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { check(); }, []);

  async function repair() {
    setWorking(true); setMessage("");
    try { const result = await apiRequest("/admin/database/repair", { method: "POST" }); setStatus(result); setMessage(result.message); }
    catch (error) { setMessage(error.message); }
    finally { setWorking(false); }
  }

  async function seedOrder() {
    setWorking(true); setMessage("");
    try { const result = await apiRequest("/admin/database/sample-order", { method: "POST" }); setMessage(`${result.message} Order: ${result.publicId}`); }
    catch (error) { setMessage(error.message); }
    finally { setWorking(false); }
  }

  async function loadImport() {
    try { setImportInfo(await apiRequest("/admin/supplier/import/status")); } catch (error) { setMessage(error.message); }
  }

  useEffect(() => { loadImport(); }, []);
  useEffect(() => {
    if (!importInfo?.job?.running) return undefined;
    const timer = setInterval(loadImport, 2000);
    return () => clearInterval(timer);
  }, [importInfo?.job?.running]);

  async function startImport() {
    setMessage("");
    try { await apiRequest("/admin/supplier/import", { method: "POST" }); await loadImport(); }
    catch (error) { setMessage(error.message); }
  }

  const job = importInfo?.job;

  if (loading) return <div style={{ padding: 60, color: "rgba(255,255,255,0.5)" }}>Checking MySQL tables...</div>;
  const missingTables = status?.missingTables || [];
  const missingColumns = Object.entries(status?.missingColumns || {});
  return <div style={{ maxWidth: 820 }}>
    <h2 style={{ color: "#fff", margin: "0 0 8px" }}>Database Health</h2>
    <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>Check and repair the MySQL tables used by the website.</p>
    <div style={{ ...statusCard, borderColor: status?.healthy ? "#4ade80" : "#f87171" }}>
      <strong style={{ color: status?.healthy ? "#4ade80" : "#f87171" }}>{status?.healthy ? "All required tables are ready" : "Database needs repair"}</strong>
      <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>Database: {status?.database}</span>
    </div>
    {(missingTables.length > 0 || missingColumns.length > 0) && <div style={warningStyle}><strong>Missing:</strong> {missingTables.join(", ")} {missingColumns.map(([table, columns]) => `${table} (${columns.join(", ")})`).join(", ")}</div>}
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "18px 0" }}>
      <button className="adm-btn adm-btn-primary" onClick={repair} disabled={working}>{working ? "Working..." : "Repair missing tables"}</button>
      <button className="adm-btn" onClick={check} disabled={working}>Check again</button>
      <button className="adm-btn" onClick={seedOrder} disabled={working}>Create sample pending order</button>
    </div>
    {message && <p style={{ color: message.includes("failed") || message.includes("required") ? "#f87171" : "#4ade80", fontSize: 13 }}>{message}</p>}
    <div className="adm-card" style={{ marginBottom: 18 }}>
      <div className="adm-card-header"><span className="adm-card-title">Supplier catalog backup</span></div>
      <div style={{ padding: 18, color: "rgba(255,255,255,0.7)", fontSize: 13, display: "flex", flexDirection: "column", gap: 10 }}>
        <span>Saves every SinaLite product (with its options) into the local database as a permanent record.</span>
        <span>Stored: <strong>{importInfo?.stored ?? 0}</strong> products ({importInfo?.withDetails ?? 0} with options){importInfo?.lastSynced ? ` · last synced ${new Date(importInfo.lastSynced).toLocaleString()}` : ""}</span>
        {job?.running && <span style={{ color: "#fbbf24" }}>Importing... {job.done}/{job.total || "?"}</span>}
        {!job?.running && job?.finishedAt && <span style={{ color: job.error ? "#f87171" : "#4ade80" }}>{job.error ? `Import failed: ${job.error}` : `Import finished: ${job.done} products, ${job.failed} errors`}</span>}
        <div><button className="adm-btn adm-btn-primary" onClick={startImport} disabled={job?.running}>{job?.running ? "Importing..." : "Import products from API"}</button></div>
      </div>
    </div>
    <div className="adm-card"><div className="adm-card-header"><span className="adm-card-title">Required tables ({status?.requiredTables?.length || 0})</span></div><div style={{ padding: 18, display: "flex", gap: 8, flexWrap: "wrap" }}>{(status?.requiredTables || []).map(table => <span key={table} style={tableChip}>{table}</span>)}</div></div>
  </div>;
}

const statusCard = { background: "rgba(255,255,255,0.04)", border: "1px solid", borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 7 };
const warningStyle = { background: "rgba(239,68,68,0.14)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, padding: 14, fontSize: 13 };
const tableChip = { background: "rgba(0,170,221,0.12)", border: "1px solid rgba(0,170,221,0.25)", borderRadius: 5, padding: "5px 8px", color: "rgba(255,255,255,0.75)", fontFamily: "monospace", fontSize: 11 };